// src/frontend/data/books/BlockchainFundamentals/data/cap_bitcoin_intro.js

const CAP_BITCOIN_INTRO = {
  id: 0, // ajusta el id según tu índice real
  slug: "cap-bitcoin-intro",
  title: "Qué es Bitcoin?",
  sections: [
    {
      heading: "Recordando la lección anterior",
      text: [
        "En la lección anterior vimos que las **criptomonedas** tienen las propiedades necesarias para funcionar como dinero: pueden usarse como medio de intercambio, unidad de cuenta y reserva de valor, todo ello en formato puramente digital.",
        "También comentamos que existen **miles de criptomonedas** distintas, cada una con objetivos, comunidades y niveles de riesgo diferentes. Esa variedad plantea una pregunta natural: si todas son digitales y se pueden enviar por Internet, **¿cuál tiene sentido estudiar primero?**",
        "Antes de entrar a proyectos más específicos, conviene empezar por el origen de todo: una criptomoneda que cambió la conversación sobre el dinero digital y que sigue siendo referencia más de una década después de su nacimiento."
      ]
    },
    {
      heading: "Primero, hablemos de Bitcoin",
      text: [
        "La primera referencia pública a **Bitcoin (BTC)** aparece el **1 de noviembre de 2008** en una lista de correo de criptografía. Bajo el seudónimo de **Satoshi Nakamoto**, su creador (o grupo de creadores) comparte un documento técnico donde describe un sistema de efectivo electrónico entre pares.",
        "En ese texto, Bitcoin se presenta como:",
        "- Una criptomoneda capaz de **evitar el doble gasto** en una red **peer-to-peer (P2P)**.",
        "- Un sistema que **no requiere bancos centrales ni intermediarios de confianza**.",
        "- Una red donde los participantes pueden mantener un **alto grado de pseudonimato**, usando claves y direcciones en lugar de nombres reales.",
        "Para lograrlo, el diseño de Bitcoin combina varios ingredientes: criptografía de clave pública, funciones de hash, una red distribuida de nodos y un protocolo de consenso basado en **Proof of Work (PoW)**."
      ]
    },
    {
      heading: "Del concepto al sistema monetario en línea",
      text: [
        "En **2009**, el software de Bitcoin se pone en marcha y la red comienza a funcionar. Se mina el **bloque génesis**, el primer bloque de la cadena, y con ello arranca un libro mayor público que cualquier persona puede verificar.",
        "Bitcoin se convierte en la **primera moneda digital funcional** con la que se puede enviar y recibir valor a través de Internet sin aprobar nada con un banco, sin pasar por pasarelas de pago tradicionales y sin pedir permiso a una autoridad central.",
        "A partir de ahí se crean miles de otros proyectos, pero Bitcoin permanece como **referencia histórica y técnica**: fue el primer sistema que demostró que un dinero puramente digital, abierto y sin un dueño único podía funcionar a escala global."
      ]
    },
    {
      heading: "Cómo funciona, muy resumido",
      text: [
        "Sin entrar todavía en todos los detalles técnicos, podemos resumir el funcionamiento de Bitcoin en pocas ideas clave:"
      ],
      list: [
        "Existe una **red de nodos** repartidos por todo el mundo que comparten y validan la misma base de datos: la cadena de bloques de Bitcoin.",
        "Las transacciones se agrupan en **bloques**, y cada bloque hace referencia criptográfica al bloque anterior, formando una cadena inmutable.",
        "Un subconjunto de nodos, llamados **mineros**, compiten para añadir el siguiente bloque resolviendo un problema de cálculo (Proof of Work).",
        "El minero que gana la carrera propone un nuevo bloque. Si el resto de la red lo acepta como válido, se añade a la cadena y el minero recibe una **recompensa en BTC**, más las comisiones de las transacciones incluidas.",
        "Esa recompensa por bloque se va reduciendo con el tiempo (evento conocido como **halving**), lo que limita la emisión y contribuye a la escasez programada de Bitcoin."
      ]
    },
    {
      heading: "Características interesantes de Bitcoin",
      text: [
        "Algunas propiedades de Bitcoin ayudan a entender por qué sigue siendo tan mencionado cuando se habla de criptomonedas:"
      ],
      list: [
        "La emisión total está limitada a **21 millones de BTC**. Nunca habrá más allá de esa cifra en el protocolo original.",
        "Un solo bitcoin se puede fraccionar en **100,000,000 de unidades más pequeñas**, llamadas **satoshis** (0.00000001 BTC cada una).",
        "La red funciona sin una autoridad central: no hay un banco ni una empresa que pueda decidir unilateralmente quién puede usar Bitcoin.",
        "El protocolo es **código abierto**, lo que permite que cualquier persona lo estudie, lo audite o ejecute su propio nodo para verificar las reglas.",
        "La contabilidad es **pública y verificable**: cualquier transacción confirmada puede consultarse en exploradores de bloques.",
        "Es una red **global**: mientras haya conexión a Internet (o a canales alternativos que reenvíen las transacciones), alguien puede enviar y recibir BTC desde prácticamente cualquier parte del mundo.",
        "Gracias al uso de criptografía y a su diseño de consenso, es resistente a la censura y difícil de detener siempre que haya suficientes nodos distribuidos."
      ]
    },
    {
      heading: "¿Por qué empezar estudiando Bitcoin?",
      text: [
        "Entre miles de criptomonedas distintas, Bitcoin sigue siendo un buen punto de partida porque reúne varios aspectos clave:",
        "Es la **primera implementación exitosa** de una blockchain pública con dinero nativo.",
        "Gran parte de la terminología y de los conceptos técnicos (bloques, minería, Proof of Work, halving, nodos, billeteras) se entienden mejor a través de su ejemplo.",
        "Muchas otras redes se describen a sí mismas como “más rápidas”, “más privadas” o “más escalables” que Bitcoin; para evaluar si eso es cierto, primero conviene entender qué hace exactamente Bitcoin y cuáles son sus límites.",
        "En las siguientes lecciones iremos desglosando estos elementos con más calma, pero por ahora quédate con la idea central: **Bitcoin inauguró una nueva forma de dinero digital**, sin intermediarios y con reglas claras en el protocolo, y eso abrió la puerta a todo el ecosistema que hoy llamamos blockchain y criptoactivos."
      ]
    },
     {
      heading: "Una visión temprana de Satoshi Nakamoto",
      text: [
        "Muy al inicio del proyecto, Satoshi Nakamoto dejó clara su intuición sobre el futuro del dinero digital. En enero de 2009 escribió:",
        "“Me sorprendería que dentro de 10 años no utilicemos moneda electrónica de alguna manera. Ahora que conocemos una forma de hacerlo, inevitablemente no se va a requerir la intervención de un tercero de confianza”.",
        "— Satoshi Nakamoto, 17 de enero de 2009",
        "Esta frase resume la idea central: si ya existe una forma técnica de tener dinero electrónico sin intermediarios de confianza, es solo cuestión de tiempo para que se use de manera habitual."
      ]
    },

    {
      heading: "La propuesta inicial de Bitcoin",
      text: [
        "Cuando Satoshi diseñó Bitcoin, tenía muy presentes los intentos previos de dinero digital, como DigiCash de David Chaum. Muchos de esos proyectos se enfrentaron a un problema crítico: el **doble gasto**, es decir, usar la misma unidad de valor dos veces sin que el sistema lo detecte.",
        "En la práctica, el doble gasto hacía que la mayoría de propuestas de moneda digital terminaran siendo **centralizadas**: se necesitaba una entidad que llevara un registro único y definitivo de todos los saldos para evitar fraudes.",
        "La novedad de Bitcoin fue plantear un sistema donde **no hiciera falta un punto central de control** para evitar el doble gasto. Para ello, Satoshi combinó dos ideas clave:"
      ],
      list: [
        "La **Prueba de Trabajo (Proof of Work, PoW)**, para que los nodos compitan honestamente por proponer el siguiente bloque.",
        "El uso de **bloques de transacciones** encadenados entre sí mediante hashes, formando una cadena de bloques verificable."
      ],
      textAfter: [
        "Al unir estas piezas, se definió lo que hoy conocemos como **blockchain** o cadena de bloques: un registro distribuido, protegido por criptografía y mantenido por muchos participantes en red, sin que ninguno tenga control absoluto.",
        "En los primeros mensajes, Satoshi imaginaba que Bitcoin podría comenzar en nichos pequeños donde se valorara mucho la privacidad y los micropagos:"
      ]
    },

    {
      heading: "Primeros casos de uso imaginados por Satoshi",
      text: [
        "Satoshi no esperaba que, desde el primer día, Bitcoin se convirtiera en un sistema financiero global. Su visión inicial apuntaba a usos concretos en entornos muy específicos:",
        "“Podría comenzar en un nicho reducido como puntos de recompensa, tokens de donación, moneda para un juego o micropagos para sitios de adultos”.",
        "— Satoshi Nakamoto, 17 de enero de 2009",
        "La idea era sencilla: empezar allí donde los métodos de pago tradicionales eran incómodos, caros o poco privados, y desde ahí ir creciendo si el sistema demostraba ser útil y resistente."
      ]
    },

    {
      heading: "Bitcoins no son archivos: qué “posee” realmente un usuario",
      text: [
        "Un malentendido muy común es imaginar que los bitcoin son **archivos** que se guardan en tu computadora o teléfono, como si fueran documentos o fotos.",
        "En realidad, el enfoque es muy distinto: cuando hablamos de bitcoin, nos referimos a la **unidad de cuenta del sistema**, es decir, a los saldos que aparecen en el libro contable que mantiene la blockchain.",
        "Lo que cada usuario controla en la práctica es una **clave criptográfica** que le da poder sobre una o varias direcciones. Esas direcciones funcionan como “números de cuenta” dentro de la red.",
        "La blockchain —el núcleo de Bitcoin— es el libro contable distribuido que registra todos los movimientos. Tus fondos no están “dentro” de tu dispositivo: están registrados en ese libro global, y tu clave es lo que te permite moverlos."
      ]
    },

    {
      heading: "Bitcoin (con B mayúscula): la red",
      text: [
        "Cuando escribimos **Bitcoin** con B mayúscula, nos referimos al **sistema completo**:",
        "la red peer-to-peer (P2P) de nodos, el protocolo, las reglas de consenso y la infraestructura que permite enviar y recibir valor sin intermediarios.",
        "En esta red:",
        "🔹 Se pueden transmitir transacciones de un usuario a otro.",
        "🔹 Los nodos validan y comparten información.",
        "🔹 Todas las operaciones se almacenan de forma seudónima en un libro de cuentas llamado **blockchain**.",
        "Bitcoin (la red) es, por tanto, una plataforma sobre la que se construyen aplicaciones de transferencia de valor, pagos, resguardo de fondos y muchas otras posibilidades."
      ]
    },

    {
      heading: "bitcoin (BTC, con b minúscula): la unidad de valor",
      text: [
        "Cuando escribimos **bitcoin** en minúscula, solemos hablar de la **unidad de cuenta** que circula sobre la red.",
        "Algunos puntos importantes:",
        "🔹 El suministro máximo está acotado: **nunca existirán más de 21 millones de bitcoin**.",
        "🔹 Esos 21 millones se liberan gradualmente a lo largo de muchos años, mediante el proceso de minería.",
        "🔹 Cada bitcoin se puede dividir en unidades más pequeñas para facilitar pagos de cualquier tamaño.",
        "Por costumbre, cuando se habla de la moneda se usa el código **BTC**, y en el uso más estricto del lenguaje no se pone en plural: lo correcto sería decir “5 bitcoin” y no “5 bitcoins”, aunque en el habla cotidiana ambos usos aparecen."
      ]
    },

    {
      heading: "Blockchain: el libro de cuentas distribuido",
      text: [
        "La **blockchain** de Bitcoin es el **libro de cuentas público y distribuido** donde se registran todas las transacciones y saldos de la red.",
        "Cada bloque contiene un conjunto de transacciones, un hash propio y el hash del bloque anterior. Esta estructura encadenada hace que modificar el historial sea computacionalmente muy costoso.",
        "Gracias al trabajo de los mineros y a las reglas de consenso, la blockchain de Bitcoin se considera una de las bases de datos más **seguras, auditables y resistentes a la manipulación** que existen en la actualidad."
      ]
    },

    {
      heading: "Minería de Bitcoin",
      text: [
        "La **minería** es el proceso mediante el cual las transacciones de la red Bitcoin son:",
        "🔹 Verificadas según las reglas del protocolo.",
        "🔹 Agrupadas en nuevos bloques.",
        "🔹 Encadenadas al historial existente mediante Prueba de Trabajo (PoW).",
        "Para ello se utilizan técnicas avanzadas de criptografía y **hardware especializado**, comúnmente llamado “equipos mineros”. Durante este proceso, los mineros:",
        "🔹 Compiten por resolver un problema criptográfico (encontrar un hash válido).",
        "🔹 Proponen nuevos bloques a la red.",
        "🔹 Reciben una **recompensa en bitcoin recién emitidos**, más las comisiones de las transacciones incluidas.",
        "Así, la minería cumple una doble función: **asegura la red** y, al mismo tiempo, es el mecanismo de **emisión controlada** de nuevos bitcoin."
      ]
    },

    {
      heading: "Satoshis (SAT): la unidad más pequeña",
      text: [
        "La unidad mínima registrable en la cadena de bloques de Bitcoin se llama **satoshi** (SAT), en honor a Satoshi Nakamoto.",
        "Un satoshi equivale a **0.00000001 bitcoin**, es decir, la cien millonésima parte de 1 BTC.",
        "Esto significa que:",
        "🔹 1 BTC = 100,000,000 satoshis (100 millones de SAT).",
        "🔹 Puedes enviar y recibir fracciones muy pequeñas de bitcoin, lo que facilita micropagos o cantidades muy precisas.",
        "Gracias a esta divisibilidad tan fina, Bitcoin puede usarse tanto para montos grandes como para pagos muy pequeños, sin necesidad de crear otra moneda para los centavos."
      ]
    }
  ]
};

export default CAP_BITCOIN_INTRO;
